import { Injectable } from '@angular/core';

import { merge, Observable, of, shareReplay, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { RSSFeedApiService } from '../../../api/rssFeed/rss-feed.api.service';
import { RSSFeed } from '../models/rss-feed.model';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { BriefingsApi } from '../../../api/briefings/briefings.api';
import { BriefingListItemModel } from '../../../Common/MonitorShared/model/briefing-list-item.model';
import { AllResourceAbstractService } from '../../../Common/Basic/service/all-resource-abstract.service';
import { RSSFeedApi } from '../../../api/rssFeed/models/rss-feed-api.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { ResourceAction } from '../../../Common/Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';

@Injectable()
export class RSSFeedService extends AllResourceAbstractService<RSSFeed, RSSFeedApi, RSSFeed, RSSFeedApi> {

    all$: Observable<RSSFeed[]> = merge(
        this.getApiService().getList().pipe(
            map(list => list?.map(item => this.constructListModel(item)))
        ),
        this.refreshAllManualSubject
    ).pipe(
        shareReplay(1)
    );

    feedToBriefingsMap$ = this.all$.pipe(
        switchMap(list => {
            if (list?.length > 0) {
                const ids = list.map(feed => feed.id);
                return this.briefingApiService.getByFeedIds(ids);
            } else {
                return of(null);
            }
        }),
        map(briefingListItems => {
            if (briefingListItems) {
                const briefingListItemModels = briefingListItems?.map(listItem => new BriefingListItemModel(listItem));
                return this.mapFeedIdsToBriefings(briefingListItemModels);
            }
        })
    );

    constructor(
        private coreDialogService: CoreDialogService,
        private rssFeedApiService: RSSFeedApiService,
        private briefingApiService: BriefingsApi,
        private messageService: MessageService,
        private localizationService: LocalizationService,

    ) {
        super();
    }

    getApiService(): MultiBaseAbstractApi<RSSFeedApi, RSSFeedApi> {
        return this.rssFeedApiService;
    };

    constructResourceModel(apiModel: RSSFeedApi): RSSFeed {
        return new RSSFeed(apiModel);
    }

    constructListModel(apiModel: RSSFeedApi): RSSFeed {
        return new RSSFeed(apiModel);
    }


    create(feed: RSSFeed): Observable<number> {
        return this.createResource(feed).pipe(
            switchMap((id) =>
                this.refreshAllManual().pipe(map(x => id))
            )
        );
    }


    confirmBulkDelete(deleteFeeds: RSSFeed[]) {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'RSSFeeds.deleteDialog.heading',
                alertType: messageSeverityEnum.error,
                alertMessage: 'RSSFeeds.deleteDialog.alertMessage',
                showCancel: true,
                message: 'RSSFeeds.deleteDialog.message',
                list: deleteFeeds.map(feed => feed.name),
                okLabel: 'Core.button.delete',
                confirmAction: () => this.bulkDeleteResource(deleteFeeds).pipe(
                    switchMap(() => this.refreshAllManual())
                )
            },
            outputs: {
                dialogClose: () => {}
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    update(feed: RSSFeed): Observable<number> {
        return this.updateResource(
            feed,
            { actionType: ResourceActionTypes.UPDATE }
        ).pipe(
            switchMap((id) => this.refreshAllManual().pipe(map(x => id)))
        );
    }


    override getById(id: number): Observable<RSSFeed> {
        return this.all$.pipe(
            map((feeds: RSSFeed[]) => {
                return feeds.find(x => x.id === id);
            }),
            take(1)
        );
    }


    getByIds(ids: number[]): Observable<RSSFeed[]> {
        if (!ids?.length) {
            return of([]);
        }

        return this.all$.pipe(
            map((feeds: RSSFeed[]) => feeds.filter(feed => ids.includes(feed.id))),
            take(1)
        );
    }


    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as RSSFeed;

        switch (actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'RSSFeeds.create.error.heading',
                        detail: this.localizationService.transform('RSSFeeds.create.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'RSSFeeds.create.success.heading',
                        detail: this.localizationService.transform('RSSFeeds.create.success.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'RSSFeeds.update.error.heading',
                        detail: this.localizationService.transform('RSSFeeds.update.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'RSSFeeds.update.success.heading',
                        detail: this.localizationService.transform('RSSFeeds.update.success.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.BULK_DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'RSSFeeds.bulkDelete.error.heading',
                        detail: 'RSSFeeds.bulkDelete.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'RSSFeeds.bulkDelete.success.heading',
                        detail: 'RSSFeeds.bulkDelete.success.message'
                    });
                }

                break;
            }
        }
    }

    private mapFeedIdsToBriefings(briefings: BriefingListItemModel[]): Map<number, BriefingListItemModel[]> {

        const feedToBriefingsMap = new Map<number, BriefingListItemModel[]>();

        briefings.forEach(briefing => {
            if (!briefing.feedIds || !briefing.feedIds.length) {
                return;
            }
            briefing.feedIds.forEach(feedId => {
                let briefingsWithFeed = feedToBriefingsMap.get(feedId);
                if (!briefingsWithFeed) {
                    feedToBriefingsMap.set(feedId, []);
                    briefingsWithFeed = [];
                }
                briefingsWithFeed.push(briefing);
                feedToBriefingsMap.set(feedId, briefingsWithFeed);
            });
        });

        return feedToBriefingsMap;
    }
}
